import React, { useEffect, useMemo, useState } from "react";
import {
    Alert,
    Box,
    Chip,
    CircularProgress,
    Container,
    Divider,
    Paper,
    Typography
} from "@mui/material";
import {
    CheckCircleOutline,
    HourglassBottom,
    InfoOutlined,
    WarningAmber
} from "@mui/icons-material";
import { useSearchParams } from "react-router-dom";
import { getShopCheckoutStatus } from "../api/shopApi";
import { getEventCheckoutStatus } from "../api/eventApi";
import { checkPaymentStatus } from "../api/planApi";
import { formatCurrency } from "../utils/currency";

const formatMoney = (value, currency = "EUR") => formatCurrency(value, currency);
const getStatusVisual = (statusRaw) => {
    const status = String(statusRaw || "").toUpperCase();
    if (status === "PAID") {
        return {
            severity: "success",
            title: "Payment completed",
            subtitle: "Your payment is confirmed."
        };
    }
    if (status === "ABANDONED" || status === "VOIDED" || status === "CANCELLED" || status === "FAILED") {
        return {
            severity: "warning",
            title: "Payment cancelled",
            subtitle: "You can return and try payment again."
        };
    }
    return {
        severity: "info",
        title: "Payment processing",
        subtitle: "Status is being confirmed in background."
    };
};

const AfterCheckout = () => {
    const [searchParams] = useSearchParams();
    const isShopFlow = searchParams.get("shop") === "1";
    const isEventFlow = searchParams.get("event") === "1";
    const source = searchParams.get("source");
    const isPlanSource = source === "plan";

    const [isLoading, setIsLoading] = useState(true);
    const [shopStatus, setShopStatus] = useState(null);
    const [shopError, setShopError] = useState("");
    const [eventStatus, setEventStatus] = useState(null);
    const [eventError, setEventError] = useState("");
    const [planStatus, setPlanStatus] = useState(null);
    const [planError, setPlanError] = useState("");

    const orderToken = searchParams.get("order-token");
    const queryRef = searchParams.get("ref");

    const fallbackRef = useMemo(() => {
        if (queryRef) return queryRef;
        if (isShopFlow) {
            return localStorage.getItem("shop_pending_order_ref") || localStorage.getItem("shop_last_order_ref");
        }
        if (isEventFlow) {
            return localStorage.getItem("event_pending_order_ref") || localStorage.getItem("event_last_order_ref");
        }
        return localStorage.getItem("plan_pending_order_ref") || localStorage.getItem("plan_last_order_ref");
    }, [queryRef, isShopFlow, isEventFlow]);

    useEffect(() => {
        const load = async () => {
            const hasPaymentContext = Boolean(orderToken || fallbackRef || isPlanSource || isShopFlow);
            if (!hasPaymentContext) {
                setTimeout(() => setIsLoading(false), 1200);
                return;
            }

            setIsLoading(true);
            setShopError("");
            setEventError("");
            setPlanError("");
            try {
                if (isShopFlow) {
                    const payload = orderToken
                        ? { orderToken }
                        : { merchantReference: fallbackRef };
                    const response = await getShopCheckoutStatus(payload);
                    setShopStatus(response);

                    if (response?.paymentStatus === "paid" || response?.paymentStatus === "cancelled") {
                        localStorage.removeItem("shop_pending_order_ref");
                    }
                } else if (isEventFlow) {
                    const payload = orderToken
                        ? { orderToken }
                        : { merchantReference: fallbackRef };
                    const response = await getEventCheckoutStatus(payload);
                    setEventStatus(response);
                    if (response?.paymentStatus === "paid" || response?.paymentStatus === "cancelled") {
                        localStorage.removeItem("event_pending_order_ref");
                    }
                } else {
                    const response = await checkPaymentStatus(orderToken || null, fallbackRef || null);
                    setPlanStatus(response);
                    const normalizedStatus = String(response?.paymentStatus || "").toUpperCase();
                    if (normalizedStatus === "PAID" || normalizedStatus === "ABANDONED" || normalizedStatus === "VOIDED") {
                        localStorage.removeItem("plan_pending_order_ref");
                    }
                }
            } catch (error) {
                console.error("AfterCheckout payment status error:", error);
                const message = error?.response?.data?.message || "Could not verify payment status yet.";
                if (isShopFlow) {
                    setShopError(message);
                } else if (isEventFlow) {
                    setEventError(message);
                } else {
                    setPlanError(message);
                }
            } finally {
                setIsLoading(false);
            }
        };

        void load();
    }, [isShopFlow, isEventFlow, isPlanSource, orderToken, fallbackRef]);

    if (isLoading) {
        return (
            <Container
                maxWidth="md"
                sx={{
                    my: 4,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "70vh"
                }}
            >
                <CircularProgress size={56} sx={{ mb: 2 }} />
                <Typography variant="h6">Checking payment status...</Typography>
            </Container>
        );
    }

    return (
        <Container maxWidth="md" sx={{ my: 4 }}>
            <Paper elevation={3} sx={{ p: 4, borderRadius: 2, backgroundColor: "#fff" }}>
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", mb: 3 }}>
                    <InfoOutlined color="primary" sx={{ fontSize: 64, mb: 2 }} />

                    <Typography variant="h4" fontWeight="bold" gutterBottom align="center">
                        {isShopFlow ? "Shop Checkout Result" : isEventFlow ? "Event Checkout Result" : "Payment Result"}
                    </Typography>

                    {isShopFlow || isEventFlow ? (
                        <Typography variant="body1" color="text.secondary" align="center" sx={{ mb: 2 }}>
                            Payment status is validated server-side for reliable iOS and Android PWA behavior.
                        </Typography>
                    ) : (
                        <Typography variant="body1" color="text.secondary" align="center" sx={{ mb: 2 }}>
                            Payment status is validated in backend and transaction table.
                        </Typography>
                    )}
                </Box>

                {isShopFlow ? (
                    <Box sx={{ mb: 3 }}>
                        {shopError ? (
                            <Alert severity="warning">{shopError}</Alert>
                        ) : (
                            <>
                                {(() => {
                                    const visual = getStatusVisual(shopStatus?.paymentStatus);
                                    return (
                                        <Paper
                                            elevation={0}
                                            sx={{
                                                p: 2,
                                                mb: 2,
                                                borderRadius: 2,
                                                border: "1px solid",
                                                borderColor: `${visual.severity}.main`,
                                                backgroundColor: `${visual.severity}.lighter`
                                            }}
                                        >
                                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, flexWrap: "wrap" }}>
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                    {shopStatus?.paymentStatus === "paid" ? (
                                                        <CheckCircleOutline color="success" />
                                                    ) : shopStatus?.paymentStatus === "cancelled" ? (
                                                        <WarningAmber color="warning" />
                                                    ) : (
                                                        <HourglassBottom color="info" />
                                                    )}
                                                    <Box>
                                                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                                            {visual.title}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary">
                                                            {visual.subtitle}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                                <Chip
                                                    color={visual.severity}
                                                    label={(shopStatus?.paymentStatus || "pending").toUpperCase()}
                                                />
                                            </Box>
                                        </Paper>
                                    );
                                })()}
                                <Alert
                                    severity={shopStatus?.paymentStatus === "paid" ? "success" : shopStatus?.paymentStatus === "cancelled" ? "warning" : "info"}
                                    sx={{ mb: 2 }}
                                >
                                    Payment status: <strong>{shopStatus?.paymentStatus || "pending"}</strong>
                                </Alert>
                                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                        Order reference: <strong>{shopStatus?.merchantReference || "-"}</strong>
                                    </Typography>
                                    <Box
                                        sx={{
                                            display: "grid",
                                            gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
                                            gap: 1
                                        }}
                                    >
                                        <Paper variant="outlined" sx={{ p: 1.5 }}>
                                            <Typography variant="caption" color="text.secondary">Total</Typography>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{formatMoney(shopStatus?.totalAmount, shopStatus?.currency)}</Typography>
                                        </Paper>
                                        <Paper variant="outlined" sx={{ p: 1.5 }}>
                                            <Typography variant="caption" color="text.secondary">Credit</Typography>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{formatMoney(shopStatus?.creditAmount, shopStatus?.currency)}</Typography>
                                        </Paper>
                                        <Paper variant="outlined" sx={{ p: 1.5 }}>
                                            <Typography variant="caption" color="text.secondary">Montonio</Typography>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{formatMoney(shopStatus?.montonioAmount, shopStatus?.currency)}</Typography>
                                        </Paper>
                                    </Box>
                                </Paper>
                            </>
                        )}
                    </Box>
                ) : isEventFlow ? (
                    <Box sx={{ mb: 3 }}>
                        {eventError ? (
                            <Alert severity="warning">{eventError}</Alert>
                        ) : (
                            <>
                                {(() => {
                                    const visual = getStatusVisual(eventStatus?.paymentStatus);
                                    return (
                                        <Paper
                                            elevation={0}
                                            sx={{
                                                p: 2,
                                                mb: 2,
                                                borderRadius: 2,
                                                border: "1px solid",
                                                borderColor: `${visual.severity}.main`,
                                                backgroundColor: `${visual.severity}.lighter`
                                            }}
                                        >
                                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, flexWrap: "wrap" }}>
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                    {eventStatus?.paymentStatus === "paid" ? (
                                                        <CheckCircleOutline color="success" />
                                                    ) : eventStatus?.paymentStatus === "cancelled" ? (
                                                        <WarningAmber color="warning" />
                                                    ) : (
                                                        <HourglassBottom color="info" />
                                                    )}
                                                    <Box>
                                                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                                            {visual.title}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary">
                                                            {visual.subtitle}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                                <Chip
                                                    color={visual.severity}
                                                    label={(eventStatus?.paymentStatus || "pending").toUpperCase()}
                                                />
                                            </Box>
                                        </Paper>
                                    );
                                })()}
                                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                        Order reference: <strong>{eventStatus?.merchantReference || "-"}</strong>
                                    </Typography>
                                    <Box
                                        sx={{
                                            display: "grid",
                                            gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
                                            gap: 1
                                        }}
                                    >
                                        <Paper variant="outlined" sx={{ p: 1.5 }}>
                                            <Typography variant="caption" color="text.secondary">Total</Typography>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{formatMoney(eventStatus?.totalAmount, eventStatus?.currency)}</Typography>
                                        </Paper>
                                        <Paper variant="outlined" sx={{ p: 1.5 }}>
                                            <Typography variant="caption" color="text.secondary">Credit</Typography>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{formatMoney(eventStatus?.creditAmount, eventStatus?.currency)}</Typography>
                                        </Paper>
                                        <Paper variant="outlined" sx={{ p: 1.5 }}>
                                            <Typography variant="caption" color="text.secondary">Montonio</Typography>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{formatMoney(eventStatus?.montonioAmount, eventStatus?.currency)}</Typography>
                                        </Paper>
                                    </Box>
                                </Paper>
                            </>
                        )}
                    </Box>
                ) : (
                    <Box sx={{ mb: 3 }}>
                        {planError ? (
                            <Alert severity="warning">{planError}</Alert>
                        ) : (
                            <>
                                {(() => {
                                    const visual = getStatusVisual(planStatus?.paymentStatus);
                                    return (
                                        <Paper
                                            elevation={0}
                                            sx={{
                                                p: 2,
                                                mb: 2,
                                                borderRadius: 2,
                                                border: "1px solid",
                                                borderColor: `${visual.severity}.main`,
                                                backgroundColor: `${visual.severity}.lighter`
                                            }}
                                        >
                                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, flexWrap: "wrap" }}>
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                    {String(planStatus?.paymentStatus || "").toUpperCase() === "PAID" ? (
                                                        <CheckCircleOutline color="success" />
                                                    ) : (String(planStatus?.paymentStatus || "").toUpperCase() === "ABANDONED" ||
                                                        String(planStatus?.paymentStatus || "").toUpperCase() === "VOIDED" ||
                                                        String(planStatus?.paymentStatus || "").toUpperCase() === "CANCELLED") ? (
                                                        <WarningAmber color="warning" />
                                                    ) : (
                                                        <HourglassBottom color="info" />
                                                    )}
                                                    <Box>
                                                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                                            {visual.title}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary">
                                                            {visual.subtitle}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                                <Chip
                                                    color={visual.severity}
                                                    label={(planStatus?.paymentStatus || "PENDING").toUpperCase()}
                                                />
                                            </Box>
                                        </Paper>
                                    );
                                })()}
                                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                                    <Typography variant="body2">
                                        Merchant reference: <strong>{planStatus?.merchantReference || fallbackRef || "-"}</strong>
                                    </Typography>
                                </Paper>
                            </>
                        )}
                    </Box>
                )}

                <Divider sx={{ my: 3 }} />

                <Paper sx={{ p: 3, backgroundColor: "#f8f9fa", border: "1px solid #e9ecef" }}>
                    <Typography variant="body2" color="text.secondary" align="center">
                        Important: if you do not receive confirmation email within 15 minutes, check spam folder or contact support.
                    </Typography>
                </Paper>
            </Paper>
        </Container>
    );
};

export default AfterCheckout;